import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, ChevronDown, Check, X, Minus } from 'lucide-react';
import { COMPARE, COMPANY } from '../config/siteContent';

/**
 * CompareSection - light theme, INTERACTIVE redesign (May 2026).
 *
 * Two layered comparisons:
 *
 *   1. Numbered question cards. Click to reveal iConnect's answer
 *      (most are "Yes - ...with a concrete reason"). Visual contrast
 *      between the question and iConnect's answer makes the differentiator
 *      tangible.
 *
 *   2. Comparison matrix below: iConnect vs. Generic VAN vs. Big-Brand VAN
 *      across 7 capability rows. yes / partial / no / extra-cost are
 *      rendered as icons so the matrix scans fast.
 *
 *   3. Closer callout with phone + email CTAs.
 */
const CompareSection = () => {
  const [openIdx, setOpenIdx] = useState(null);

  const toggle = (i) => setOpenIdx((cur) => (cur === i ? null : i));

  return (
    <section
      id="compare"
      className="relative py-24 lg:py-32 bg-bg-soft overflow-hidden"
    >
      {/* Heading spans the full section width so it can sit on one line at large
          sizes; the comparison content below stays at max-w-6xl (unchanged). */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0.5, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 max-w-3xl xl:max-w-none mx-auto"
        >
          <p className="eyebrow">{COMPARE.eyebrow}</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-4 tracking-[-0.015em] text-balance xl:whitespace-nowrap">
            <span className="text-gradient-sky">Ask</span>{' '}
            your current provider these questions
          </h2>
          <p className="text-text-secondary">
            Most VANs stall on speed and support. Click any question to see
            iConnect's answer.
          </p>
        </motion.div>
      </div>

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        {/* Interactive question cards */}
        <div className="space-y-3 mb-16">
          {COMPARE.questions.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0.5, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ duration: 0.4, delay: idx * 0.04 }}
                className="card-elevated overflow-hidden"
              >
                <button
                  onClick={() => toggle(idx)}
                  className="w-full p-5 lg:p-6 flex items-start gap-4 text-left group"
                  aria-expanded={isOpen}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-sky shadow-glow-sky flex items-center justify-center font-mono text-sm font-bold text-white">
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                  <div className="flex-1">
                    <p className="text-text-primary text-base lg:text-lg leading-relaxed">
                      {item.q}
                    </p>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`flex-shrink-0 text-text-muted mt-2 transition-transform group-hover:text-accent-primary ${
                      isOpen ? 'rotate-180 text-accent-primary' : ''
                    }`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 lg:px-6 lg:pb-6 pl-[72px]">
                        <div className="bg-accent-tint border-l-4 border-accent-primary rounded-r-lg p-4">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Check
                              size={16}
                              className="text-accent-primary"
                              strokeWidth={3}
                            />
                            <span className="font-mono text-[11px] uppercase tracking-widest text-accent-deep font-bold">
                              iConnect
                            </span>
                          </div>
                          <p className="text-text-primary leading-relaxed">
                            {item.iConnect}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Comparison matrix */}
        <motion.div
          initial={{ opacity: 0.5, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="eyebrow text-center">Side-by-side</p>
          <h3 className="font-display font-bold text-2xl sm:text-3xl text-text-primary text-center mb-8 tracking-tight">
            iConnect vs. the alternatives
          </h3>

          <div className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border-subtle">
                    {COMPARE.matrix.headers.map((h, i) => (
                      <th
                        key={i}
                        className={`p-4 lg:p-5 text-sm font-display font-bold ${
                          i === 1
                            ? 'text-accent-primary bg-accent-tint'
                            : 'text-text-secondary'
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.matrix.rows.map((row, rIdx) => (
                    <tr
                      key={rIdx}
                      className="border-b border-border-subtle last:border-0"
                    >
                      {row.map((cell, cIdx) => (
                        <td
                          key={cIdx}
                          className={`p-4 lg:p-5 text-sm ${
                            cIdx === 0
                              ? 'font-semibold text-text-primary'
                              : cIdx === 1
                              ? 'bg-accent-tint/40'
                              : ''
                          }`}
                        >
                          {cIdx === 0 ? (
                            cell
                          ) : (
                            <MatrixCell value={cell} highlight={cIdx === 1} />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        {/* Closer callout */}
        <motion.div
          initial={{ opacity: 0.5, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5 }}
          className="relative card-elevated p-8 lg:p-12 text-center overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-50 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(55,172,228,0.10) 0%, transparent 70%)',
            }}
          />
          <div className="relative">
            <p className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-text-primary mb-2 leading-tight">
              If the answer to any of these is{' '}
              <span className="text-gradient-sky">"No"</span> - 
            </p>
            <p className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-text-primary mb-8 leading-tight">
              May be time to evaluate a more flexible EDI provider.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <a
                href={`tel:${COMPANY.phones.salesTollFree.replace(/\D/g, '')}`}
                className="btn-primary group"
              >
                <Phone size={16} />
                Call Sales: {COMPANY.phones.salesTollFree}
              </a>
              <Link to="/contact" className="btn-secondary">
                {COMPARE.closerCTA}
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/**
 * MatrixCell - renders a yes/partial/no/extra-cost/etc. answer with the
 * appropriate icon. Highlight column (iConnect) gets bolder treatment.
 */
const MatrixCell = ({ value, highlight }) => {
  const map = {
    yes: { Icon: Check, color: highlight ? 'text-accent-primary' : 'text-emerald-500', label: 'Yes' },
    no: { Icon: X, color: 'text-rose-500', label: 'No' },
    partial: { Icon: Minus, color: 'text-amber-500', label: 'Partial' },
    sometimes: { Icon: Minus, color: 'text-amber-500', label: 'Sometimes' },
    rare: { Icon: X, color: 'text-rose-500', label: 'Rare' },
    'extra cost': { Icon: Minus, color: 'text-amber-500', label: 'Extra cost' },
    'business hours': { Icon: Minus, color: 'text-amber-500', label: 'Business hours' },
    'tiered support': { Icon: Minus, color: 'text-amber-500', label: 'Tiered' },
    days: { Icon: Check, color: 'text-emerald-500', label: 'Days' },
    weeks: { Icon: Minus, color: 'text-amber-500', label: 'Weeks' },
    months: { Icon: X, color: 'text-rose-500', label: 'Months' },
  };
  const config = map[value] || { Icon: Minus, color: 'text-text-muted', label: value };
  const { Icon, color, label } = config;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon size={16} className={`${color} flex-shrink-0`} strokeWidth={2.5} />
      <span className={highlight ? 'font-semibold text-text-primary' : 'text-text-secondary'}>
        {label}
      </span>
    </span>
  );
};

export default CompareSection;
